import { useCallback, useEffect, useRef } from 'react'

/**
 * A React hook for managing cached values in sessionStorage with automatic expiration.
 *
 * This hook provides a simple interface to store, retrieve, and manage values in sessionStorage
 * with built-in time-to-live (TTL) functionality. Values are automatically removed from storage
 * after the specified TTL expires. The cached value persists across component remounts and page
 * refreshes (until the browser session ends or TTL expires).
 *
 * @template T - The type of the cached value. Defaults to `string`.
 *
 * @param key - The sessionStorage key used to store the cached value. Must be unique per cached value.
 * @param options - Configuration options for the cached value.
 * @param options.ttl - Time-to-live in milliseconds. After this duration, the value is automatically
 *                      removed from cache. Defaults to 5 minutes (300000ms).
 * @param options.parser - A function to parse the raw string value from sessionStorage into type `T`.
 *                         Defaults to a simple cast. Return `null` to indicate parsing failure.
 *
 * @returns An object containing methods to interact with the cached value:
 * - `get()` - Retrieves the current cached value. Returns `null` if no value exists. If the in-memory
 *             value is `null`, attempts to reload from sessionStorage.
 * - `set(value)` - Sets a new value in the cache and sessionStorage. Resets the TTL timer. Any
 *                  previous expiration timer is cancelled.
 * - `rem()` - Removes the value from both cache and sessionStorage. Cancels any pending expiration timer.
 * - `isEmpty()` - Returns `true` if the current cached value is `null`.
 * - `isNotEmpty()` - Returns `true` if the current cached value is not `null`.
 *
 * @example
 * ```typescript
 * // Basic usage with string
 * const cache = useCachedValue('user-token')
 * cache.set('abc123')
 * const token = cache.get() // 'abc123'
 *
 * // With custom type and parser
 * const numberCache = useCachedValue<number>('count', {
 *   ttl: 60000, // 1 minute
 *   parser: (v) => v ? parseInt(v, 10) : null
 * })
 * numberCache.set(42)
 *
 * // With complex objects
 * interface UserData {
 *   id: number
 *   name: string
 * }
 * const userCache = useCachedValue<UserData>('user-data', {
 *   parser: (v) => {
 *     try {
 *       return v ? JSON.parse(v) : null
 *     } catch {
 *       return null
 *     }
 *   }
 * })
 * userCache.set({ id: 1, name: 'John' })
 * ```
 *
 * @remarks
 * - Values are stored in sessionStorage as strings using `String(value)`. For complex types,
 *   provide a custom parser that handles serialization (e.g., JSON.parse/stringify).
 * - The TTL timer is reset every time `set()` is called with a non-null value.
 * - Setting a value to `null` removes it from storage and cancels the expiration timer.
 * - All timers are automatically cleaned up when the component unmounts.
 * - SessionStorage operations are wrapped in try-catch blocks to handle edge cases like
 *   storage quota exceeded or private browsing mode restrictions.
 * - The hook maintains an in-memory reference to the value, so calling `get()` multiple
 *   times doesn't repeatedly access sessionStorage unless the value is null.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage | MDN sessionStorage}
 */
export const useCachedValue = <T = string>(
    key: string,
    {
        ttl = 5 * 60 * 1000,
        parser = (v) => v as T | null,
    }: {
        ttl?: number
        parser?(rawValue?: string | null): T | null
    } = {},
) => {
    // Store timer ID to allow cleanup
    const timerRef = useRef<NodeJS.Timeout | null>(null)

    // Initialize value only once using useRef with null check
    const value = useRef<T | null>(null)
    const initialized = useRef(false)

    if (!initialized.current) {
        try {
            value.current = parser(sessionStorage.getItem(key))
        } catch (error) {
            value.current = null
        }
        initialized.current = true
    }

    // Cleanup timer on unmount or when key changes
    useEffect(
        () => () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        },
        [key],
    )

    const setValue = useCallback(
        <ValueType extends T | null>(nextValue: ValueType) => {
            // Clear any existing timer
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }

            if (nextValue !== value.current) {
                value.current = nextValue

                if (nextValue === null) {
                    sessionStorage.removeItem(key)
                } else {
                    sessionStorage.setItem(key, String(nextValue))
                }
            }

            if (nextValue !== null) {
                timerRef.current = setTimeout(() => {
                    value.current = null
                    try {
                        sessionStorage.removeItem(key)
                    } catch {}
                    timerRef.current = null
                }, ttl)
            }

            return nextValue
        },
        [key, ttl],
    )

    const get = useCallback((): T | null => {
        if (value.current !== null) return value.current

        try {
            return setValue(parser(sessionStorage.getItem(key)))
        } catch {
            return null
        }
    }, [key, parser, setValue])

    const set = useCallback((v: T) => setValue(v), [setValue])
    const rem = useCallback(() => setValue(null), [setValue])
    const isEmpty = useCallback(() => value.current === null, [])
    const isNotEmpty = useCallback(() => !isEmpty(), [])

    return {
        get,
        set,
        rem,
        // utility methods
        isEmpty,
        isNotEmpty,
    }
}
